import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import SimulatedBanner from "@/components/SimulatedBanner";
import {
  Users, ChevronLeft, Plus, Upload, Trash2, Loader2, FileText,
} from "lucide-react";
import { toast } from "sonner";

export default function EmailListDetail() {
  const { listId } = useParams();
  const [list, setList] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", email: "" });
  const [csvText, setCsvText] = useState("");
  const [saving, setSaving] = useState(false);
  const [importReport, setImportReport] = useState(null);
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const [l, c] = await Promise.all([
        api.get(`/email/lists/${listId}`),
        api.get(`/email/lists/${listId}/contacts`),
      ]);
      setList(l.data);
      setContacts(c.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [listId]);

  const addContact = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/email/lists/${listId}/contacts`, addForm);
      toast.success("Contacto añadido");
      setAddOpen(false);
      setAddForm({ name: "", email: "" });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    } finally {
      setSaving(false);
    }
  };

  const removeContact = async (c) => {
    if (!window.confirm(`¿Eliminar contacto ${c.email}?`)) return;
    try {
      await api.delete(`/email/lists/${listId}/contacts/${c.id}`);
      toast.success("Contacto eliminado");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result || ""));
    reader.readAsText(file);
  };

  const doImport = async () => {
    if (!csvText.trim()) {
      toast.error("Pegue un CSV o suba un archivo");
      return;
    }
    setSaving(true);
    setImportReport(null);
    try {
      const { data } = await api.post(`/email/lists/${listId}/contacts/import`, { csv_text: csvText });
      setImportReport(data);
      toast.success(`Importados ${data.inserted} contactos`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in-up" data-testid="list-detail-page">
      <div>
        <Link to="/email-marketing" className="text-xs text-brand-emerald-dark hover:underline flex items-center gap-1 mb-3">
          <ChevronLeft className="h-3.5 w-3.5" /> Volver a Email Marketing
        </Link>
        <div className="divider-gold mb-3" />
        <h1 className="font-serif-display text-4xl text-brand-ink font-semibold flex items-center gap-3">
          <Users className="h-8 w-8 text-brand-emerald" />
          {list?.name || "Lista"}
        </h1>
        {list?.description && <p className="text-sm text-stone-500 mt-2">{list.description}</p>}
      </div>

      <div className="flex gap-2 flex-wrap">
        <Button onClick={() => setAddOpen(true)} data-testid="contact-add-btn" className="bg-brand-emerald text-white hover:bg-brand-emerald-dark">
          <Plus className="h-4 w-4 mr-1.5" /> Añadir contacto
        </Button>
        <Button variant="outline" onClick={() => { setImportOpen(true); setImportReport(null); setCsvText(""); }} data-testid="contact-import-btn">
          <Upload className="h-4 w-4 mr-1.5" /> Importar CSV
        </Button>
      </div>

      {loading ? (
        <div className="card-elevated p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" /></div>
      ) : contacts.length === 0 ? (
        <div className="card-elevated p-12 text-center text-stone-500">Sin contactos en esta lista.</div>
      ) : (
        <div className="card-elevated overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50/60">
                <TableHead>Nombre</TableHead>
                <TableHead>Correo</TableHead>
                <TableHead>Etiquetas</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contacts.map((c) => (
                <TableRow key={c.id} data-testid={`contact-row-${c.email}`}>
                  <TableCell className="font-medium text-brand-ink">{c.name}</TableCell>
                  <TableCell className="text-sm">{c.email}</TableCell>
                  <TableCell>
                    <div className="flex gap-1 flex-wrap">
                      {(c.tags || []).map((t) => (
                        <Badge key={t} className="bg-stone-100 text-stone-700 border border-stone-200 text-xs">{t}</Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => removeContact(c)} className="text-destructive hover:bg-destructive/10">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Add contact dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="contact-add-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Añadir contacto</DialogTitle>
          </DialogHeader>
          <form onSubmit={addContact} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">Nombre</Label>
              <Input required value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} data-testid="contact-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">Correo</Label>
              <Input required type="email" value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} data-testid="contact-email-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="contact-submit" className="bg-brand-emerald text-white">Añadir</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import CSV dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-2xl" data-testid="csv-import-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Importar CSV</DialogTitle>
            <DialogDescription>Formato: columnas <code>nombre,email</code> (con o sin encabezado).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <input type="file" accept=".csv,text/csv" ref={fileRef} className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="csv-file-input" />
              <Button variant="outline" onClick={() => fileRef.current?.click()}>
                <FileText className="h-4 w-4 mr-2" /> Seleccionar archivo…
              </Button>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">O pegue el CSV</Label>
              <Textarea rows={8} value={csvText} onChange={(e) => setCsvText(e.target.value)} placeholder="nombre,email&#10;Juan Perez,juan@ejemplo.com" className="font-mono text-xs" data-testid="csv-textarea" />
            </div>
            {importReport && (
              <div className="rounded-lg border border-border p-4 bg-brand-emerald-soft/40 text-sm space-y-1" data-testid="import-report">
                <div>✅ <strong>{importReport.inserted}</strong> contactos insertados</div>
                <div>♻️ <strong>{importReport.duplicates_in_list}</strong> ya existían en la lista (omitidos)</div>
                <div>⚠️ <strong>{importReport.invalid.length}</strong> filas inválidas</div>
                {importReport.invalid.length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs text-stone-600">Ver detalles</summary>
                    <ul className="mt-1 text-xs text-stone-500 list-disc pl-4">
                      {importReport.invalid.slice(0, 20).map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Cerrar</Button>
            <Button onClick={doImport} disabled={saving} data-testid="csv-import-submit" className="bg-brand-gold text-brand-ink hover:bg-brand-gold-dark hover:text-white shadow-gold">
              {saving ? "Importando…" : "Importar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
